import {
  normalizePromoCode,
  promoCodeCreateRequestSchema,
  promoCodeUpdateRequestSchema,
  PROMO_CODE_MAX_LENGTH,
  type GiftablePlan,
  type PromoCode,
  type PromoCodeCreateInput,
  type PromoCodeCreateRequest,
  type PromoCodeKind,
  type PromoCodeUpdateRequest,
} from '@manaro/contracts'
import { backendDay } from './labels.ts'

/** Quick picks in the dialog: the usual deals. */
export const PERCENT_PRESETS = [10, 20, 30, 50] as const
export const PAYMENT_PRESETS = [1, 3, 6, 12] as const
export const MONTH_PRESETS = [1, 2, 3, 6] as const

// No 0/O and 1/I: the code is read out over the phone and typed from a screenshot.
const CODE_ALPHABET = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'
const GENERATED_LENGTH = 8

export interface PromoForm {
  kind: PromoCodeKind
  code: string
  discountPercent: string
  discountPayments: number
  plan: GiftablePlan
  months: number
  maxRedemptions: string
  expiresAt: string
  note: string
}

export const EMPTY_PROMO_FORM: PromoForm = {
  kind: 'discount',
  code: '',
  discountPercent: '20',
  discountPayments: 1,
  plan: 'start',
  months: 1,
  maxRedemptions: '',
  expiresAt: '',
  note: '',
}

export function generateCode(random: () => number = Math.random): string {
  return Array.from({ length: GENERATED_LENGTH }, () => CODE_ALPHABET[Math.floor(random() * CODE_ALPHABET.length)]).join('')
}

/** What the code field shows while typing: the backend's own normalisation, cut to the length it accepts. */
export const typedCode = (raw: string): string => normalizePromoCode(raw).slice(0, PROMO_CODE_MAX_LENGTH)

const optionalNumber = (value: string): number | null => (value.trim() === '' ? null : Number(value))
const requiredNumber = (value: string): number | undefined => (value.trim() === '' ? undefined : Number(value))

export function toCreateInput(form: PromoForm): PromoCodeCreateInput {
  const common = {
    code: form.code,
    maxRedemptions: optionalNumber(form.maxRedemptions),
    expiresAt: form.expiresAt || null,
    note: form.note.trim() || null,
  }

  return form.kind === 'discount'
    ? {
        kind: 'discount',
        ...common,
        // An empty field stays undefined so the schema asks for a percent instead of reporting «from 1%».
        discountPercent: requiredNumber(form.discountPercent) as number,
        discountPayments: form.discountPayments,
      }
    : { kind: 'free_months', ...common, plan: form.plan, months: form.months }
}

type Parsed<T> = { ok: true; request: T } | { ok: false; error: string }

export function parseCreateForm(form: PromoForm, today: string): Parsed<PromoCodeCreateRequest> {
  const parsed = promoCodeCreateRequestSchema.safeParse(toCreateInput(form))
  if (!parsed.success) return { ok: false, error: parsed.error.issues[0]?.message ?? 'Проверьте условия промокода.' }
  if (parsed.data.expiresAt && parsed.data.expiresAt < today) {
    return { ok: false, error: 'Дата окончания — не раньше сегодняшнего дня.' }
  }
  return { ok: true, request: parsed.data }
}

export interface LimitsForm {
  maxRedemptions: string
  expiresAt: string
  note: string
}

export const limitsFormOf = (promo: PromoCode): LimitsForm => ({
  maxRedemptions: promo.maxRedemptions === null ? '' : String(promo.maxRedemptions),
  expiresAt: promo.expiresAt ? backendDay(promo.expiresAt) : '',
  note: promo.note ?? '',
})

/** Only the fields the person changed, so a stale form never overwrites someone else's edit of another field. */
export function parseLimitsForm(form: LimitsForm, promo: PromoCode, today: string): Parsed<PromoCodeUpdateRequest> | null {
  const before = limitsFormOf(promo)
  const changes: PromoCodeUpdateRequest = {}

  if (form.maxRedemptions.trim() !== before.maxRedemptions) changes.maxRedemptions = optionalNumber(form.maxRedemptions)
  if (form.expiresAt !== before.expiresAt) {
    if (form.expiresAt && form.expiresAt < today) return { ok: false, error: 'Дата окончания — не раньше сегодняшнего дня.' }
    changes.expiresAt = form.expiresAt || null
  }
  if (form.note.trim() !== before.note.trim()) changes.note = form.note.trim() || null

  if (Object.keys(changes).length === 0) return null

  const parsed = promoCodeUpdateRequestSchema.safeParse(changes)
  return parsed.success
    ? { ok: true, request: parsed.data }
    : { ok: false, error: parsed.error.issues[0]?.message ?? 'Проверьте лимит и дату.' }
}

const pad = (value: number): string => String(value).padStart(2, '0')

/** YYYY-MM-DD in local time, the format of <input type="date">. */
export const toDateInput = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`

/** Patches one code in the cached list instead of refetching it. */
export function replacePromo(rows: PromoCode[], promo: PromoCode): PromoCode[] {
  return rows.some((row) => row.id === promo.id) ? rows.map((row) => (row.id === promo.id ? promo : row)) : [promo, ...rows]
}

/** «Действуют» on top of the list: what salons can still enter. */
export function splitByStatus(rows: PromoCode[]): { active: PromoCode[]; inactive: PromoCode[] } {
  return {
    active: rows.filter((row) => row.status === 'active'),
    inactive: rows.filter((row) => row.status !== 'active'),
  }
}

export function matchesSearch(promo: PromoCode, query: string): boolean {
  const needle = query.trim().toLowerCase()
  if (!needle) return true
  return promo.code.toLowerCase().includes(needle.replace(/[\s-]/g, '')) || (promo.note ?? '').toLowerCase().includes(needle)
}
